// src/components/admin/ClassChangePanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    fetchClassChangeCandidates,
    runClassChange,
    streamJobLogs,
    getJob,
} from "../../../lib/ligamanagerApi";
import { LEAGUE_OPTIONS } from "../../../lib/leagueSheetsID";

const CLASS_OPTIONS = ["PRO", "INT", "AM"];

function prettyErr(e) {
    if (!e) return "Unbekannter Fehler";
    if (typeof e === "string") return e;
    if (e instanceof Error) return e.message || "Fehler";
    if (typeof e === "object") {
        if (e.detail) return typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail, null, 2);
        if (e.message) return e.message;
        try {
            return JSON.stringify(e, null, 2);
        } catch {
            return String(e);
        }
    }
    return String(e);
}

function isDividerMsg(msg) {
    const s = String(msg || "").trim();
    if (!s) return false;
    return /^-+$/.test(s) && s.length >= 20;
}

function classifyLine(msg) {
    const s = String(msg || "");
    if (s.startsWith("[") && s.includes("]")) return "step";
    if (s.startsWith("    ")) return "indent";
    return "normal";
}

function classifyNum(num) {
    const n = Number(num);
    if (!Number.isFinite(n)) return "UNKNOWN";
    if (n >= 1 && n <= 99) return "PRO";
    if (n >= 100 && n <= 499) return "INT";
    if (n >= 500 && n <= 999) return "AM";
    return "UNKNOWN";
}

function ProgressBar({ percent, labelLeft, labelRight }) {
    const p = Math.max(0, Math.min(100, Number(percent || 0)));
    return (
        <div style={{ width: "100%", marginTop: 8 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    opacity: 0.85,
                    fontFamily: "var(--ff-Everett)",
                    fontSize: 12,
                }}
            >
                <div>{labelLeft}</div>
                <div>{labelRight}</div>
            </div>

            <div
                style={{
                    marginTop: 6,
                    height: 10,
                    borderRadius: 999,
                    border: "1px solid rgba(232,253,83,.35)",
                    background: "rgba(232,253,83,.08)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${p}%`,
                        background: "rgba(232,253,83,.75)",
                    }}
                />
            </div>
        </div>
    );
}

export default function ClassChangePanel() {
    const [sheetIdOrUrl, setSheetIdOrUrl] = useState(LEAGUE_OPTIONS[0]?.value || "");
    const [loadingCandidates, setLoadingCandidates] = useState(false);

    const [candidates, setCandidates] = useState([]);
    const [driverName, setDriverName] = useState("");
    const [targetClass, setTargetClass] = useState("PRO");
    const [newCarNumber, setNewCarNumber] = useState("");

    const [jobId, setJobId] = useState(null);
    const [error, setError] = useState("");
    const [actionBusy, setActionBusy] = useState(false);

    const [logItems, setLogItems] = useState([]);
    const [progress, setProgress] = useState({
        status: "",
        step: 0,
        total: 0,
        label: "",
    });

    const [autoScroll, setAutoScroll] = useState(true);
    const [showDetails, setShowDetails] = useState(false);
    const [filterInfo, setFilterInfo] = useState(true);
    const [filterWarn, setFilterWarn] = useState(true);
    const [filterError, setFilterError] = useState(true);
    const [filterUi, setFilterUi] = useState(true);

    const stopStreamRef = useRef(null);
    const jobPollTimerRef = useRef(null);
    const scrollBoxRef = useRef(null);
    const stickToBottomRef = useRef(true);

    const busy = actionBusy;

    function pushUiInfo(msg) {
        setLogItems((prev) => [
            ...prev,
            { seq: null, level: "INFO", ts: null, msg: String(msg || ""), uiOnly: true },
        ]);
    }

    function pushLogItem(item) {
        let level = (item?.level || "INFO").toUpperCase();
        if (!["INFO", "WARN", "ERROR"].includes(level)) level = "INFO";

        setLogItems((prev) => [
            ...prev,
            {
                seq: typeof item?.seq === "number" ? item.seq : null,
                level,
                ts: item?.ts ?? null,
                msg: item?.msg ?? "",
                uiOnly: false,
            },
        ]);
    }

    function stopStream() {
        try {
            if (stopStreamRef.current) stopStreamRef.current();
        } catch {}
        stopStreamRef.current = null;
    }

    function stopJobPolling() {
        if (jobPollTimerRef.current) {
            clearInterval(jobPollTimerRef.current);
            jobPollTimerRef.current = null;
        }
    }

    useEffect(() => {
        return () => {
            stopStream();
            stopJobPolling();
        };
    }, []);

    useEffect(() => {
        const el = scrollBoxRef.current;
        if (!el || !autoScroll || !stickToBottomRef.current) return;
        el.scrollTop = el.scrollHeight;
    }, [logItems, autoScroll]);

    function onScrollLogs() {
        const el = scrollBoxRef.current;
        if (!el) return;
        const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 40;
        stickToBottomRef.current = nearBottom;
    }

    useEffect(() => {
        let active = true;

        async function loadCandidates() {
            if (!sheetIdOrUrl.trim()) {
                setCandidates([]);
                setDriverName("");
                return;
            }

            try {
                setLoadingCandidates(true);
                setError("");
                const data = await fetchClassChangeCandidates(sheetIdOrUrl.trim());
                if (!active) return;

                const rows = Array.isArray(data) ? data : [];
                setCandidates(rows);

                if (!rows.length) {
                    setDriverName("");
                    return;
                }

                setDriverName((prev) => {
                    if (prev && rows.some((r) => r.driver_name === prev)) return prev;
                    return rows[0].driver_name || "";
                });
            } catch (e) {
                if (!active) return;
                setCandidates([]);
                setDriverName("");
                setError(prettyErr(e));
            } finally {
                if (active) setLoadingCandidates(false);
            }
        }

        loadCandidates();
        return () => {
            active = false;
        };
    }, [sheetIdOrUrl]);

    const selectedDriver = useMemo(() => {
        return candidates.find((d) => d.driver_name === driverName) || null;
    }, [candidates, driverName]);

    const currentClass = selectedDriver?.class_name || "UNKNOWN";
    const currentCarNumber = selectedDriver?.car_number ?? "";
    const targetHint = useMemo(() => classifyNum(newCarNumber), [newCarNumber]);

    function resetRunState() {
        setError("");
        setLogItems([]);
        setJobId(null);
        setProgress({ status: "", step: 0, total: 0, label: "" });
        stopStream();
        stopJobPolling();
    }

    function startJobPolling(id) {
        stopJobPolling();

        const pollOnce = async () => {
            try {
                const j = await getJob(id);
                setProgress({
                    status: j?.status || "",
                    step: Number(j?.progress_step || 0),
                    total: Number(j?.progress_total || 0),
                    label: j?.progress_label || "",
                });

                if (j?.status && ["done", "error", "canceled"].includes(String(j.status))) {
                    stopJobPolling();
                }
            } catch {
                // silently continue
            }
        };

        pollOnce();
        jobPollTimerRef.current = setInterval(pollOnce, 700);
    }

    async function handleStart() {
        resetRunState();

        if (!sheetIdOrUrl.trim()) return setError("Bitte Liga auswählen.");
        if (!driverName.trim()) return setError("Bitte Fahrer auswählen.");
        if (!targetClass.trim()) return setError("Bitte Zielklasse auswählen.");
        if (!newCarNumber.trim()) return setError("Bitte neue Fahrzeugnummer eintragen.");

        setActionBusy(true);

        try {
            pushUiInfo("Class Change wird gestartet …");

            const res = await runClassChange({
                sheetInput: sheetIdOrUrl.trim(),
                driverName: driverName.trim(),
                targetClass: targetClass.trim().toUpperCase(),
                newCarNumber: newCarNumber,
            });

            if (!res?.job_id) {
                throw new Error("Backend hat kein job_id zurückgegeben.");
            }

            setJobId(res.job_id);
            pushUiInfo(`Job gestartet: #${res.job_id}`);
            pushUiInfo("Live Logs verbinden …");

            startJobPolling(res.job_id);

            const stopFn = streamJobLogs({
                job_id: res.job_id,
                onLog: (item) => pushLogItem(item),
                onDone: (status) => {
                    pushUiInfo(`Job beendet: ${status}`);
                    if (String(status).trim() === "error") {
                        setError("Job beendet mit Fehler. Siehe Logs.");
                    }
                    setActionBusy(false);
                    stopStream();
                    stopJobPolling();
                },
                onError: (e) => {
                    const msg = prettyErr(e);
                    pushUiInfo(`SSE FEHLER: ${msg}`);
                    setError(msg);
                    setActionBusy(false);
                    stopStream();
                    stopJobPolling();
                },
            });

            stopStreamRef.current = stopFn;
        } catch (e) {
            const msg = prettyErr(e);
            setError(msg);
            pushUiInfo(`FEHLER: ${msg}`);
            setActionBusy(false);
            stopStream();
            stopJobPolling();
        }
    }

    function onClearLogs() {
        setLogItems([]);
        setError("");
    }

    const renderedItems = useMemo(() => {
        const allowLevel = (lvl, uiOnly) => {
            const L = (lvl || "INFO").toUpperCase();
            if (uiOnly) return !!filterUi;
            if (L === "INFO") return !!filterInfo;
            if (L === "WARN") return !!filterWarn;
            if (L === "ERROR") return !!filterError;
            return true;
        };

        return logItems
            .filter((it) => allowLevel(it.level, !!it.uiOnly))
            .map((it, idx) => {
                const msg = String(it.msg ?? "");
                let level = (it.level || "INFO").toUpperCase();
                if (!["INFO", "WARN", "ERROR"].includes(level)) level = "INFO";

                return {
                    key: `${it.seq ?? "x"}-${idx}`,
                    level,
                    ts: it.ts,
                    seq: it.seq,
                    msg,
                    isDivider: isDividerMsg(msg),
                    kind: classifyLine(msg),
                    uiOnly: !!it.uiOnly,
                    rawJson: JSON.stringify(
                        { seq: it.seq, level, ts: it.ts, msg: it.msg, uiOnly: it.uiOnly },
                        null,
                        0
                    ),
                };
            });
    }, [logItems, filterInfo, filterWarn, filterError, filterUi]);

    const progressPercent =
        progress.total > 0 ? Math.round((Math.max(0, progress.step) / Math.max(1, progress.total)) * 100) : 0;

    return (
        <div className="card card--withbar">
            <h2 style={{ marginTop: 0 }}>Class Change</h2>

            <div style={{ display: "grid", gap: 12, width: "100%", maxWidth: 980 }}>
                <div className="callout">
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Aktion</div>
                    <div style={{ display: "grid", gap: 6, fontFamily: "var(--ff-Everett)" }}>
                        <div>
                            Wähle Liga, Fahrer, Zielklasse und neue Fahrzeugnummer.
                        </div>
                    </div>
                </div>

                <label style={{ width: "100%" }}>
                    <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                        Liga
                    </div>
                    <select
                        className="input"
                        value={sheetIdOrUrl}
                        onChange={(e) => setSheetIdOrUrl(e.target.value)}
                        style={{ width: 260 }}
                        disabled={busy}
                    >
                        {LEAGUE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                        Fahrer
                    </div>
                    <select
                        className="input"
                        value={driverName}
                        onChange={(e) => setDriverName(e.target.value)}
                        style={{ width: 320 }}
                        disabled={busy || loadingCandidates || !candidates.length}
                    >
                        {!candidates.length ? (
                            <option value="">Keine Fahrer gefunden</option>
                        ) : (
                            candidates.map((d) => (
                                <option key={d.driver_name} value={d.driver_name}>
                                    {d.driver_name}
                                </option>
                            ))
                        )}
                    </select>
                </label>

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <label>
                        <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                            Aktuelle Fahrzeugnummer
                        </div>
                        <input
                            className="input"
                            value={currentCarNumber}
                            readOnly
                            style={{ width: 180 }}
                        />
                    </label>

                    <label>
                        <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                            Aktuelle Klasse
                        </div>
                        <input
                            className="input"
                            value={currentClass}
                            readOnly
                            style={{ width: 160 }}
                        />
                    </label>
                </div>

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <label>
                        <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                            Zielklasse
                        </div>
                        <select
                            className="input"
                            value={targetClass}
                            onChange={(e) => setTargetClass(e.target.value)}
                            style={{ width: 160 }}
                            disabled={busy}
                        >
                            {CLASS_OPTIONS.map((cls) => (
                                <option key={cls} value={cls}>
                                    {cls}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label>
                        <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                            Neue Fahrzeugnummer
                        </div>
                        <input
                            className="input"
                            type="number"
                            min="1"
                            max="999"
                            value={newCarNumber}
                            onChange={(e) => setNewCarNumber(e.target.value)}
                            style={{ width: 180 }}
                            disabled={busy}
                        />
                    </label>

                    <label>
                        <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                            Klasse aus neuer Nummer
                        </div>
                        <input
                            className="input"
                            value={targetHint}
                            readOnly
                            style={{ width: 180 }}
                        />
                    </label>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                        className="button button--secondary"
                        onClick={handleStart}
                        disabled={
                            busy ||
                            !sheetIdOrUrl.trim() ||
                            !driverName.trim() ||
                            !targetClass.trim() ||
                            !newCarNumber.trim()
                        }
                    >
                        {busy ? "Class Change…" : "Class Change starten"}
                    </button>

                    <button
                        className="button button--secondary"
                        onClick={onClearLogs}
                        disabled={busy && !logItems.length}
                        type="button"
                    >
                        Logs leeren
                    </button>
                </div>

                {jobId && (
                    <div style={{ opacity: 0.9, fontFamily: "var(--ff-Everett)" }}>
                        Job: <b>#{jobId}</b>
                    </div>
                )}

                {jobId ? (
                    <ProgressBar
                        percent={progressPercent}
                        labelLeft={
                            progress.label
                                ? `${progress.label} (${progress.status || "..."})`
                                : progress.status
                                    ? `Status: ${progress.status}`
                                    : "Status: …"
                        }
                        labelRight={progress.total > 0 ? `${progress.step}/${progress.total}` : ""}
                    />
                ) : null}

                {error ? (
                    <div className="callout" style={{ borderColor: "rgba(255,140,140,.35)", color: "#ffb3b3" }}>
                        <b>Fehler:</b> {error}
                    </div>
                ) : null}

                <div className="callout">
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={autoScroll}
                                onChange={(e) => setAutoScroll(e.target.checked)}
                            />
                            Auto-Scroll
                        </label>

                        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={showDetails}
                                onChange={(e) => setShowDetails(e.target.checked)}
                            />
                            Details
                        </label>

                        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={filterUi}
                                onChange={(e) => setFilterUi(e.target.checked)}
                            />
                            UI
                        </label>

                        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={filterInfo}
                                onChange={(e) => setFilterInfo(e.target.checked)}
                            />
                            INFO
                        </label>

                        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={filterWarn}
                                onChange={(e) => setFilterWarn(e.target.checked)}
                            />
                            WARN
                        </label>

                        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={filterError}
                                onChange={(e) => setFilterError(e.target.checked)}
                            />
                            ERROR
                        </label>
                    </div>
                </div>

                <div
                    ref={scrollBoxRef}
                    onScroll={onScrollLogs}
                    className="logbox"
                    style={{
                        minHeight: 260,
                        maxHeight: 480,
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {renderedItems.length === 0 ? (
                        <div style={{ opacity: 0.7 }}>Noch keine Logs vorhanden.</div>
                    ) : (
                        renderedItems.map((it) => {
                            let color = "var(--text)";
                            if (it.level === "WARN") color = "var(--warn)";
                            if (it.level === "ERROR") color = "var(--err)";
                            if (it.uiOnly) color = "var(--info)";
                            if (it.isDivider) color = "rgba(232,253,83,.45)";

                            return (
                                <div
                                    key={it.key}
                                    style={{
                                        color,
                                        marginBottom: it.isDivider ? 8 : 4,
                                        paddingLeft: it.kind === "indent" ? 12 : 0,
                                        fontFamily: "var(--ff-Everett)",
                                    }}
                                >
                                    {showDetails ? it.rawJson : it.msg}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}